import type { eq } from "drizzle-orm";
import type { queryCache } from "@sqlite/schema";
import type { MobileDatabase } from "../../runtime/ports";
import { stableStringify } from "./stableStringify";

/**
 * The statement that persists one query's value. `query_cache.data` is NOT
 * NULL, and Drizzle sends both `null` and `undefined` as SQL NULL, so neither
 * may reach an insert:
 *   • `null` deletes the row. Every reader of a null-capable query (the CDN
 *     key, RevenueCat's offerings and trial) treats null and no data alike,
 *     and an offline read with no row fails over to the network error. A
 *     stored null would read back as a successful answer: the paywall would
 *     show "not available" offline instead of offering a retry.
 *   • `undefined` is no answer at all, so there is nothing to run and the
 *     previous row stays.
 */
export function createQueryCacheWrite(
  db: MobileDatabase,
  sqlite: { queryCache: typeof queryCache; eq: typeof eq },
) {
  const { queryCache, eq } = sqlite;
  return (queryKey: readonly unknown[], data: unknown) => {
    if (data === undefined) return undefined;
    const key = stableStringify(queryKey);
    if (data === null)
      return db.delete(queryCache).where(eq(queryCache.queryKey, key));

    const now = new Date();
    return db
      .insert(queryCache)
      .values({ queryKey: key, data, fetchedAt: now })
      .onConflictDoUpdate({
        target: queryCache.queryKey,
        set: { data, fetchedAt: now },
      });
  };
}
